import { subscribeAppResume } from "./appResumeService";
import { apiRequest, apiUpload } from "./apiClient";
import { createActivity, getActivityActor } from "./activityService";
import { trackEngagementDataAdded } from "./engagementService";
import { recordMeaningfulReviewAction } from "./appReviewService";
import { bildirimHerkeseYazService } from "./bildirimService";

/* ── Aktif "belgeleriDinleService" dinleyicileri (familyId bazlı) ──
   Yazma işlemlerinden (yükle/güncelle/sil/versiyon) sonra ilgili
   aile için kayıtlı dinleyicileri hemen tetikleyip listeyi anında
   yeniliyoruz — onSnapshot'ın anlık güncelleme hissini korur. */
const documentListeners = new Map();

function notifyDocumentsChanged(familyId) {
  documentListeners.get(familyId)?.forEach((fn) => {
    try { fn(); } catch {}
  });
}

function safeFileName(file, fallback = "belge") {
  const raw =
    file?.name ||
    file?.fileName ||
    file?.filename ||
    file?.originalname ||
    file?.lastModifiedName ||
    fallback;
  const clean = String(raw || fallback).trim();
  return clean || fallback;
}

function safeFileSize(file) {
  const size = Number(file?.size ?? file?.sizeBytes ?? file?.fileSize ?? 0);
  return Number.isFinite(size) && size >= 0 ? size : 0;
}

function safeFileType(file) {
  return String(file?.type || file?.mimeType || file?.fileType || "").trim();
}

function safeFileExt(file, fallback = "file") {
  const name = safeFileName(file, fallback);
  const fromName = name.includes(".") ? name.split(".").pop() : "";
  const fromMime = safeFileType(file).includes("/") ? safeFileType(file).split("/").pop() : "";
  return String(fromName || fromMime || fallback).toLowerCase().replace(/[^a-z0-9]+/g, "") || fallback;
}

function normalizeUploadedFile(uploadResponse = {}, originalFile = null) {
  const file = uploadResponse?.file || uploadResponse || {};
  const fileUrl = file.fileUrl || file.url || file.downloadUrl || uploadResponse.fileUrl || uploadResponse.url || uploadResponse.downloadUrl || "";
  const fileKey = file.fileKey || file.publicId || file.key || uploadResponse.fileKey || uploadResponse.publicId || uploadResponse.key || null;

  if (!fileUrl || !fileKey) {
    const err = new Error("Dosya sunucuya yüklendi ancak dosya bilgisi eksik döndü.");
    err.code = "upload_response_invalid";
    err.payload = uploadResponse;
    throw err;
  }

  return {
    ...file,
    fileUrl,
    url: fileUrl,
    downloadUrl: file.downloadUrl || uploadResponse.downloadUrl || fileUrl,
    fileKey,
    publicId: fileKey,
    storageDriver: file.storageDriver || uploadResponse.storageDriver || "local",
    fileName: file.fileName || uploadResponse.fileName || safeFileName(originalFile),
    mimeType: file.mimeType || uploadResponse.mimeType || safeFileType(originalFile),
    sizeBytes: file.sizeBytes ?? uploadResponse.sizeBytes ?? safeFileSize(originalFile),
  };
}

/* ── Dosyayı sunucuya yükle (backend: files.routes.js /upload) ── */
const sunucuyaYukle = async ({ dosya, familyId, userId, storageType = "documents", onProgress }) => {
  const formData = new FormData();
  formData.append("file", dosya);
  formData.append("type", storageType || "documents");
  formData.append("familyId", familyId);
  formData.append("userId", userId || "system");

  const res = await apiUpload("/files/upload", formData, { onProgress });
  return normalizeUploadedFile(res, dosya);
};

/* ── Belge Yükle ──
   tip: "ortak" | "kisisel"
   gorebilecekler: uid dizisi (kişisel belgeler için)

   Sıralama notu: liste güncellemesi (notifyDocumentsChanged) belge kaydı
   oluşturulur oluşturulmaz tetiklenir. Aktivite kaydı (createActivity)
   arka planda devam eder — UI'ı bloklamaz, "anında güncelleme" hissini
   geciktirmez.
*/
export const belgeYukleService = async ({
  familyId, dosya, dosyalar = [], ad, aciklama, kategori,
  gecerlilikTarihi, tip, sahipUid, sahipAd,
  yukleyenAd, yukleyenUid, gorebilecekler = [],
  profilAvatar = null, sahipAvatar = null, sahipProfileType = null,
  onProgress, storageType = "documents",
}) => {
  const secilenDosyalar = (Array.isArray(dosyalar) && dosyalar.length ? dosyalar : [dosya])
    .filter(Boolean)
    .slice(0, 2);
  if (!secilenDosyalar.length) throw new Error("Yüklenecek dosya bulunamadı.");

  const yuklenenDosyalar = [];
  let res;

  try {
    for (let index = 0; index < secilenDosyalar.length; index += 1) {
      const secilenDosya = secilenDosyalar[index];
      const saved = await sunucuyaYukle({
        dosya: secilenDosya,
        familyId,
        userId: sahipUid || yukleyenUid,
        storageType,
        onProgress: (pct) => {
          const dosyaYuzdesi = Math.max(0, Math.min(100, Number(pct) || 0));
          const toplamYuzde = Math.round(((index + (dosyaYuzdesi / 100)) / secilenDosyalar.length) * 100);
          onProgress?.(toplamYuzde);
        },
      });
      yuklenenDosyalar.push({ dosya: secilenDosya, saved });
    }

    const ana = yuklenenDosyalar[0];
    const dosyaKayitlari = yuklenenDosyalar.map(({ dosya: yuklenenDosya, saved }, index) => ({
      sira: index,
      dosyaUrl: saved.fileUrl,
      fileKey: saved.fileKey,
      storageDriver: saved.storageDriver,
      dosyaAdi: safeFileName(yuklenenDosya, "Belge"),
      dosyaBoyutu: safeFileSize(yuklenenDosya),
      dosyaTipi: safeFileExt(yuklenenDosya),
    }));

    const originalFileName = dosyaKayitlari[0].dosyaAdi;
    const belgeAdi = ad?.trim?.() || originalFileName;
    const belgeKategori = kategori || "document";
    const toplamDosyaBoyutu = dosyaKayitlari.reduce((toplam, item) => toplam + Number(item.dosyaBoyutu || 0), 0);

    res = await apiRequest(`/documents/${familyId}`, {
      method: "POST",
      body: JSON.stringify({
        ad: belgeAdi,
        aciklama: aciklama?.trim() || "",
        kategori: belgeKategori,
        gecerlilikTarihi: gecerlilikTarihi || null,
        tip,                          // "ortak" | "kisisel"
        sahipUid,
        sahipAd,
        sahipAvatar: sahipAvatar || null,
        sahipProfileType: sahipProfileType || null,
        yukleyenAd,
        yukleyenUid,
        gorebilecekler,               // uid dizisi — boşsa sadece yükleyen + yönetici
        dosyaUrl: ana.saved.fileUrl,
        fileKey: ana.saved.fileKey,
        storageDriver: ana.saved.storageDriver,
        dosyaAdi: originalFileName,
        dosyaBoyutu: toplamDosyaBoyutu,
        dosyaTipi: dosyaKayitlari[0].dosyaTipi,
        dosyalar: dosyaKayitlari,
        versiyonlar: [],
      }),
    });
  } catch (error) {
    await Promise.allSettled(
      yuklenenDosyalar.map(({ saved }) =>
        saved?.fileKey ? apiRequest(`/files/${saved.fileKey}`, { method: "DELETE" }) : Promise.resolve()
      )
    );
    throw error;
  }

  const ana = yuklenenDosyalar[0];
  const originalFileName = safeFileName(ana.dosya, "Belge");
  const belgeAdi = ad?.trim?.() || originalFileName;
  const belgeKategori = kategori || "document";
  const dosyaTipi = safeFileExt(ana.dosya);

  const belge = res?.document || {};
  if (belge && ana.saved?.fileUrl) {
    belge.url = belge.url || belge.dosyaUrl || ana.saved.fileUrl;
    belge.fileUrl = belge.fileUrl || belge.dosyaUrl || ana.saved.fileUrl;
    belge.downloadUrl = belge.downloadUrl || ana.saved.downloadUrl || ana.saved.fileUrl;
    belge.fileKey = belge.fileKey || ana.saved.fileKey;
  }

  try { trackEngagementDataAdded({ familyId, moduleKey: "documents", itemId: belge.id, metadata: { category: belgeKategori } }); } catch {}
  void recordMeaningfulReviewAction(yukleyenUid, {
    trigger: "document_uploaded",
    moduleKey: "documents",
    itemId: belge.id,
  }).catch(() => {});

  // Liste anında güncellensin
  notifyDocumentsChanged(familyId);

  // Bildirim yalnız başarılı create mutation'ından üretilir. Liste polling'i,
  // login veya resume hiçbir zaman yeni belge yan etkisi doğurmaz. Kalıcı belge
  // kimliği backend tarafındaki alıcı-bazlı idempotency ile tekrarları engeller.
  if (belge.id && yukleyenUid) {
    void bildirimHerkeseYazService({
      familyId,
      ekleyenUid: yukleyenUid,
      baslik: "Yeni Belge",
      aciklama: `${yukleyenAd || "Aile üyesi"}: ${belgeAdi}`,
      baslikKey: "notify.documentUploadedTitle",
      aciklamaKey: "notify.documentUploaded",
      aciklamaVars: { name: yukleyenAd || "", title: belgeAdi },
      type: "document",
      kaynak: "document",
      kaynakId: belge.id,
      gonderenUid: yukleyenUid,
      idempotencyKey: `document_${belge.id}`,
    });
  }

  // Arka plan: aktivite kaydı (createActivity hata olsa da kendi içinde
  // yakalar ve null döner — burada beklemeye gerek yok)
  createActivity({
    familyId,
    type: "document_added",
    category: "documents",
    actor: getActivityActor({ user: { uid: yukleyenUid }, name: yukleyenAd, avatar: profilAvatar }),
    payload: { documentName: belgeAdi, category: belgeKategori, ownerName: sahipAd, type: tip },
    targetPage: "belgeler",
    targetId: belge.id,
    visibility: storageType === "health" ? "private" : "family",
  });

  return {
    id: belge.id,
    url: ana.saved.fileUrl,
    publicId: ana.saved.fileKey,
    fileName: originalFileName,
    fileType: dosyaTipi,
    dosyalar: belge.dosyalar || [],
    sahipUid,
    sahipAd,
    kategori: belgeKategori,
  };
};

/* ── Versiyon Ekle ── */
export const belgeVersiyonEkleService = async ({
  familyId, belgeId, eskiVeri, dosya,
  yukleyenAd, yukleyenUid, onProgress, profilAvatar = null,
}) => {
  const saved = await sunucuyaYukle({
    dosya, familyId, userId: eskiVeri?.sahipUid || yukleyenUid, onProgress,
  });

  const versiyonDosyaAdi = safeFileName(dosya, "Belge");
  const dosyaTipi = safeFileExt(dosya);

  const yeniVersiyon = {
    dosyaUrl: eskiVeri.dosyaUrl,
    fileKey: eskiVeri.fileKey || eskiVeri.dosyaPublicId || null,
    dosyaAdi: eskiVeri.dosyaAdi,
    dosyaBoyutu: eskiVeri.dosyaBoyutu,
    dosyaTipi: eskiVeri.dosyaTipi,
    yukleyenAd,
    yukleyenUid,
    tarih: new Date().toISOString(),
    dosyalar: Array.isArray(eskiVeri.dosyalar) ? eskiVeri.dosyalar : [],
  };

  const yeniDosyaKaydi = {
    sira: 0,
    dosyaUrl: saved.fileUrl,
    fileKey: saved.fileKey,
    storageDriver: saved.storageDriver,
    dosyaAdi: versiyonDosyaAdi,
    dosyaBoyutu: safeFileSize(dosya),
    dosyaTipi,
  };

  await apiRequest(`/documents/${familyId}/${belgeId}`, {
    method: "PUT",
    body: JSON.stringify({
      dosyaUrl: saved.fileUrl,
      fileKey: saved.fileKey,
      storageDriver: saved.storageDriver,
      dosyaAdi: versiyonDosyaAdi,
      dosyaBoyutu: safeFileSize(dosya),
      dosyaTipi,
      dosyalar: [yeniDosyaKaydi],
      versiyonlar: [...(eskiVeri.versiyonlar || []), yeniVersiyon],
    }),
  });

  notifyDocumentsChanged(familyId);
};

/* ── Meta Güncelle ── */
export const belgeGuncelleService = async ({ familyId, belgeId, guncel }) => {
  await apiRequest(`/documents/${familyId}/${belgeId}`, {
    method: "PUT",
    body: JSON.stringify(guncel),
  });

  notifyDocumentsChanged(familyId);
};

/* ── Sil ──
   DELETE endpoint'i silinen belgenin kaydını (fileKey + versiyonlar dahil)
   geri döndürür — bu sayede "önce GET ile belgeyi bul" adımına gerek kalmaz.
   Liste anında güncellensin diye notify, dosya silme + aktivite kaydından
   ÖNCE tetiklenir; o ikisi arka planda devam eder. */
export const belgeSilService = async ({ familyId, belgeId, belge, user, tamAd, profilAvatar = null }) => {
  const res = await apiRequest(`/documents/${familyId}/${belgeId}`, { method: "DELETE" });
  const belgeVerisi = belge || res?.document || null;

  // Liste anında güncellensin
  notifyDocumentsChanged(familyId);

  // Arka plan: ana dosya + versiyon dosyalarını sunucu deposundan sil
  // (disk temiz kalsın) ve aktivite kaydı oluştur — UI'ı bloklamaz.
  (async () => {
    const fileKeys = [];
    if (belgeVerisi?.fileKey) fileKeys.push(belgeVerisi.fileKey);
    if (Array.isArray(belgeVerisi?.dosyalar)) {
      belgeVerisi.dosyalar.forEach((item) => {
        if (item?.fileKey) fileKeys.push(item.fileKey);
      });
    }
    if (Array.isArray(belgeVerisi?.versiyonlar)) {
      belgeVerisi.versiyonlar.forEach((v) => {
        if (v.fileKey) fileKeys.push(v.fileKey);
        if (Array.isArray(v?.dosyalar)) {
          v.dosyalar.forEach((item) => {
            if (item?.fileKey) fileKeys.push(item.fileKey);
          });
        }
      });
    }
    await Promise.all(
      [...new Set(fileKeys)].map((key) =>
        apiRequest(`/files/${key}`, { method: "DELETE" }).catch((e) =>
          console.warn("Dosya silinemedi:", key, e)
        )
      )
    );

    await createActivity({
      familyId,
      type: "document_deleted",
      category: "documents",
      actor: getActivityActor({ user, name: tamAd, avatar: profilAvatar }),
      payload: { documentName: belgeVerisi?.ad || belgeVerisi?.dosyaAdi || "" },
      targetPage: "belgeler",
      targetId: belgeId,
      visibility: "family",
    });
  })().catch((e) => console.warn("Belge silme arka plan işlemleri başarısız:", e));
};

/* ── Dinle (eski imza korunuyor: (familyId, callback) -> unsubscribe) ──
   Başlangıç/resume, yerel mutation ve backend SSE değişikliklerinde yenilenir. */
export const belgeleriDinleService = (familyId, callback) => {
  if (!familyId) return () => {};

  let cancelled = false;
  let refreshTimer = null;

  const fetchDocs = async () => {
    // Arka plandayken belge polling'i atlanır. Belgeler sık değişen bir veri
    // değil; öne dönünce anında tazelenir.
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const res = await apiRequest(`/documents/${familyId}`);
      if (!cancelled) callback(res?.documents || []);
    } catch (error) {
      console.warn("Belgeler alınamadı:", error);
      if (!cancelled) callback([]);
    }
  };

  if (!documentListeners.has(familyId)) documentListeners.set(familyId, new Set());
  documentListeners.get(familyId).add(fetchDocs);

  fetchDocs();
  const unsubscribeResume = subscribeAppResume(fetchDocs, { delayMs: 750 });
  const onDataChanged = (event) => {
    const detail = event?.detail || {};
    if (detail.resource !== "documents") return;
    if (String(detail.familyId || "") !== String(familyId)) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(fetchDocs, 150);
  };
  const onDataReconcile = (event) => {
    if (String(event?.detail?.familyId || "") !== String(familyId)) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(fetchDocs, 150);
  };
  window.addEventListener("fp:dataChanged", onDataChanged);
  window.addEventListener("fp:dataReconcile", onDataReconcile);

  return () => {
    cancelled = true;
    clearTimeout(refreshTimer);
    unsubscribeResume();
    window.removeEventListener("fp:dataChanged", onDataChanged);
    window.removeEventListener("fp:dataReconcile", onDataReconcile);
    documentListeners.get(familyId)?.delete(fetchDocs);
  };
};
